import * as Sentry from '@sentry/react';
import { getSessionId } from './observability/requestId';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// Env-aware trace sampling: full in dev, lighter in staging, lightest in prod (cost control).
const TRACES_SAMPLE_RATE =
  ({ development: 1.0, staging: 0.2 } as Record<string, number>)[import.meta.env.MODE] ?? 0.1;

// Keys whose values must never leave the browser in a Sentry event.
const SENSITIVE_KEY = /token|authorization|password|api[_-]?key|secret|access_token|refresh_token|cookie|session/i;

function scrub(obj: unknown, depth = 0): unknown {
  if (depth > 6 || obj == null) return obj;
  if (Array.isArray(obj)) return obj.map((v) => scrub(v, depth + 1));
  if (typeof obj === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      out[k] = SENSITIVE_KEY.test(k) ? '[redacted]' : scrub(v, depth + 1);
    }
    return out;
  }
  return obj;
}

/**
 * Initialize Sentry error tracking.
 * Call this once before React renders (in main.tsx).
 */
export function initSentry() {
  if (!SENTRY_DSN) return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    // Release tracking: set VITE_RELEASE (e.g. to $VERCEL_GIT_COMMIT_SHA) at build time.
    release: import.meta.env.VITE_RELEASE || undefined,
    // Never attach PII automatically. User context is set explicitly (id only by default).
    sendDefaultPii: false,
    // Route events through our own origin in production to survive ad/tracker blockers.
    // Dev has no serverless function, so send direct there.
    tunnel: import.meta.env.PROD ? '/api/sentry-tunnel' : undefined,
    // Only attach trace headers to our own origins (avoid leaking to third parties / CORS noise).
    tracePropagationTargets: [/^\//, /\.supabase\.co/],
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: TRACES_SAMPLE_RATE,
    // Replay is fully OFF here. PostHog is the single replay source (§0.3 locked decision),
    // joined to Sentry by the shared session_id tag + the posthog_session_id link below, so
    // there is no double capture and no double PII surface.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    ignoreErrors: [
      // Third-party DOM error from Radix UI / react-datepicker focus management
      /Failed to execute 'selectNode' on 'Range'/,
      // Stale chunk after deploy — handled by lazyWithRetry auto-reload
      /Failed to fetch dynamically imported module/,
      // Radix UI focus management edge case with non-Element event targets
      /\.contains is not a function/,
    ],
    beforeSend(event) {
      // Real scrubbing (the previous no-op only promised this).
      if (event.request) {
        delete event.request.query_string;
        if (event.request.headers) delete event.request.headers['Authorization'];
        if (event.request.cookies) delete event.request.cookies;
      }
      event.extra = scrub(event.extra) as Record<string, unknown> | undefined;
      event.contexts = scrub(event.contexts) as typeof event.contexts;
      return event;
    },
  });

  // Correlation spine: stamp the per-tab session id on every Sentry event so a single
  // session joins Sentry ⇄ PostHog ⇄ Axiom ⇄ BetterStack. `release` is already set above
  // from VITE_RELEASE (the commit SHA), giving all tools the same release stamp.
  Sentry.setTag('session_id', getSessionId());
}

/**
 * Set user context after login. Only the id is attached by default to limit PII;
 * pass includeEmail=true intentionally if email is needed for triage and DPA-covered.
 */
export function setUserContext(userId: string, email?: string, includeEmail = false) {
  Sentry.setUser(includeEmail && email ? { id: userId, email } : { id: userId });
}

/**
 * Tag the current organization so errors are filterable per tenant.
 */
export function setOrganizationTag(orgId: string, orgSlug: string) {
  Sentry.setTag('organization_id', orgId);
  Sentry.setTag('organization_slug', orgSlug);
}

/**
 * Clear user context on logout.
 */
export function clearUserContext() {
  Sentry.setUser(null);
}

/**
 * Capture an exception with context. Context is scrubbed before it leaves the browser.
 */
export function captureException(
  error: Error,
  context?: { source?: string; extra?: Record<string, unknown> }
) {
  Sentry.captureException(error, {
    tags: context?.source ? { source: context.source } : undefined,
    extra: context?.extra ? (scrub(context.extra) as Record<string, unknown>) : undefined,
  });
}

// Re-export Sentry for direct access if needed
export { Sentry };
